import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { CATEGORY_COLORS } from '../../constants/categoryColors';
import { LOG_TYPES } from '../../constants/logTypeRegistry';

const QuickCheckInCapturePreview = ({
  showCapturePreviewSection = true,
  hasNoteText = false,
  shouldShowFrontendCapturePreview = false,
  shouldShowReviewNeededPreview = false,
  shouldShowBackendSmartCapturePreview = false,
  detectedQuickNoteLabels = [],
  detectedQuickNoteEntities = [],
  isDebugMode = false,
  showDebugDetails = true,
  quickLogRoutingPreview = {},
  shouldUseBackendExtraction = false,
  quickLogDebugPayload = null,
}) => {
  const shouldShowPreview = showCapturePreviewSection
    && hasNoteText
    && (
      shouldShowFrontendCapturePreview
      || shouldShowReviewNeededPreview
      || shouldShowBackendSmartCapturePreview
    );
  const signals = Array.isArray(quickLogRoutingPreview?.signals)
    ? quickLogRoutingPreview.signals
    : [];

  return (
    <>
      {shouldShowPreview ? (
        <Box
          sx={{
            mt: 0.85,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.55,
            px: 1.05,
            py: 0.85,
            borderRadius: '18px',
            bgcolor: shouldShowBackendSmartCapturePreview
              ? 'rgba(59, 130, 246, 0.07)'
              : shouldShowFrontendCapturePreview
                ? 'rgba(111, 165, 148, 0.10)'
                : 'rgba(245, 248, 251, 0.92)',
            border: `1px solid ${shouldShowBackendSmartCapturePreview
              ? 'rgba(59, 130, 246, 0.18)'
              : shouldShowFrontendCapturePreview
                ? 'rgba(111, 165, 148, 0.28)'
                : '#dde5ee'}`,
            color: shouldShowBackendSmartCapturePreview
              ? '#1e3a8a'
              : shouldShowFrontendCapturePreview
                ? '#2F5A49'
                : '#516073',
          }}
        >
          {shouldShowBackendSmartCapturePreview ? (
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Smart Capture preview
            </Typography>
          ) : shouldShowReviewNeededPreview ? (
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Multiple updates detected
            </Typography>
          ) : shouldShowFrontendCapturePreview ? (
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Ready to capture as event
            </Typography>
          ) : (
            null
          )}
          {shouldShowFrontendCapturePreview ? (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {(detectedQuickNoteLabels.length > 0 ? detectedQuickNoteLabels : []).map((label) => {
                  const matchKey = detectedQuickNoteEntities.find((entity) => (LOG_TYPES[entity.category]?.displayLabel || entity.category) === label)?.category || 'log';
                  const chipColors = CATEGORY_COLORS[matchKey] ? CATEGORY_COLORS[matchKey] : CATEGORY_COLORS.log;
                  return (
                    <Chip
                      key={label}
                      size="small"
                      label={label}
                      sx={{
                        height: 22,
                        borderRadius: '999px',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        bgcolor: chipColors.bg,
                        color: chipColors.text,
                        border: `1px solid ${chipColors.border}`,
                      }}
                    />
                  );
                })}
              </Box>
              <Typography sx={{ fontSize: '0.76rem', color: '#516073', lineHeight: 1.35, mt: 0.15 }}>
                We found structured details, so this can be captured as one or more entries.
              </Typography>
            </>
          ) : shouldShowReviewNeededPreview ? (
            <Typography sx={{ fontSize: '0.76rem', color: '#516073', lineHeight: 1.35, mt: 0.15 }}>
              We’ll organize this into entries you can review.
            </Typography>
          ) : shouldShowBackendSmartCapturePreview ? (
            <Typography sx={{ fontSize: '0.76rem', color: '#516073', lineHeight: 1.35, mt: 0.15 }}>
              We'll break this into structured entries for review.
            </Typography>
          ) : (
            null
          )}
        </Box>
      ) : null}

      {isDebugMode && showDebugDetails && hasNoteText && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            borderRadius: '14px',
            border: '1px dashed #c9d3df',
            bgcolor: '#f8fafc',
            color: '#334155',
          }}
        >
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.5 }}>
            Smart Capture debug
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.45 }}>
            Route preview: <strong>{quickLogRoutingPreview.route}</strong>
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.45 }}>
            Reason: <strong>{quickLogRoutingPreview.reason}</strong>
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.45 }}>
            Signals: <strong>{signals.length > 0 ? signals.join(', ') : 'none'}</strong>
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.45 }}>
            Backend: <strong>{shouldUseBackendExtraction ? 'extractQuickLogEntries' : 'local only'}</strong>
          </Typography>
          {quickLogDebugPayload && (
            <Box
              sx={{
                mt: 0.75,
                p: 1,
                borderRadius: '12px',
                bgcolor: '#ffffff',
                border: '1px solid #d9e2ec',
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, mb: 0.5 }}>
                Backend response preview
              </Typography>
              <Typography component="pre" sx={{
                m: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.72rem',
                lineHeight: 1.45,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              }}>
                {JSON.stringify(quickLogDebugPayload, null, 2)}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default QuickCheckInCapturePreview;
